import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Image as ImageIcon, X } from 'lucide-react';
import type { TrainingPageDef } from '@hcc/types';
import { ApiError } from '../../../lib/api';
import {
  useGenerateAiCourse,
  useGenerateAiPage,
  type AiCourseDraft,
  type AiImagePayload,
} from '../../../hooks/useTrainingModules';
import { ModalShell } from '../../ui/ModalShell';
import { Button } from '../../ui/Button';
import { SHARED_INPUT_CLASS } from './pageMeta';

type Mode = 'page' | 'course';

const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;

/** Read a File as base64 (no data: prefix) plus its media type, for the API. */
function fileToImage(file: File): Promise<AiImagePayload> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('could not read the image'));
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve({
        data: comma >= 0 ? result.slice(comma + 1) : result,
        media_type: (ACCEPTED.includes(file.type as (typeof ACCEPTED)[number])
          ? file.type
          : 'image/png') as AiImagePayload['media_type'],
      });
    };
    reader.readAsDataURL(file);
  });
}

/**
 * "Generate with AI" for the training-module builder. Two modes: recreate one
 * page from a photo of an old slide and/or a description, or generate a whole
 * course from source text. The result is handed back to the editor as an
 * editable draft (inserted pages, or seeded module fields) — nothing is
 * published; the admin reviews and edits before publishing as normal.
 */
export function AiBuilderModal({
  open,
  onClose,
  hasContent,
  graded = true,
  onInsertPages,
  onSeedCourse,
}: {
  open: boolean;
  onClose: () => void;
  /** Whether the module already has pages (course mode replaces them). */
  hasContent: boolean;
  /** The module's marking mode, so generated pages match it. */
  graded?: boolean;
  onInsertPages: (pages: TrainingPageDef[]) => void;
  onSeedCourse: (draft: AiCourseDraft) => void;
}) {
  const [mode, setMode] = useState<Mode>('page');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState<{ payload: AiImagePayload; name: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const genPage = useGenerateAiPage();
  const genCourse = useGenerateAiCourse();
  const busy = genPage.isPending || genCourse.isPending;

  function reset() {
    setDescription('');
    setImage(null);
  }

  function close() {
    if (busy) return;
    reset();
    onClose();
  }

  async function onPickFile(file: File | undefined) {
    if (!file) return;
    if (!ACCEPTED.includes(file.type as (typeof ACCEPTED)[number])) {
      toast.error('Use a PNG, JPEG, WebP or GIF image');
      return;
    }
    if (file.size > 5_000_000) {
      toast.error('Image must be under 5 MB');
      return;
    }
    try {
      setImage({ payload: await fileToImage(file), name: file.name });
    } catch {
      toast.error('Could not read that image');
    }
  }

  async function generate() {
    try {
      if (mode === 'page') {
        if (!description.trim() && !image) {
          toast.error('Add a description or an image');
          return;
        }
        const res = await genPage.mutateAsync({
          graded,
          description: description.trim() || undefined,
          image: image?.payload,
        });
        onInsertPages(res.pages);
        toast.success(`Added ${res.pages.length} page${res.pages.length === 1 ? '' : 's'}`);
        close();
      } else {
        if (!description.trim()) {
          toast.error('Describe the course or paste the material');
          return;
        }
        if (hasContent && !window.confirm('This replaces the current pages. Continue?')) return;
        const res = await genCourse.mutateAsync(description.trim());
        onSeedCourse(res.draft);
        toast.success('Course generated as a draft');
        close();
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'The AI builder could not generate that');
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={close}
      titleId="ai-builder-title"
      maxWidth="max-w-lg"
      busy={busy}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-accent" />
          <h2 id="ai-builder-title" className="text-lg font-semibold text-fg">
            Generate with AI
          </h2>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {(
            [
              { value: 'page', label: 'One page', hint: 'From a photo or a description' },
              { value: 'course', label: 'Whole course', hint: 'From your source material' },
            ] as const
          ).map((o) => {
            const on = mode === o.value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => setMode(o.value)}
                aria-pressed={on}
                className={`rounded-token-sm border p-2.5 text-left text-sm transition ${
                  on
                    ? 'border-accent bg-accent/10'
                    : 'border-line/10 bg-surface-2 hover:border-line-strong/20'
                }`}
              >
                <span className="block font-medium text-fg">{o.label}</span>
                <span className="block text-xs text-fg-dim">{o.hint}</span>
              </button>
            );
          })}
        </div>

        {mode === 'page' ? (
          <div>
            <span className="text-sm font-medium text-fg-muted">Photo of the slide (optional)</span>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPTED.join(',')}
              className="hidden"
              onChange={(e) => void onPickFile(e.target.files?.[0] ?? undefined)}
            />
            {image ? (
              <div className="mt-1 flex items-center gap-2 rounded-token-sm border border-line/10 bg-surface-2 p-2 text-sm">
                <ImageIcon className="h-4 w-4 text-fg-muted" />
                <span className="min-w-0 flex-1 truncate text-fg">{image.name}</span>
                <button
                  type="button"
                  onClick={() => setImage(null)}
                  className="text-fg-dim hover:text-fg"
                  aria-label="Remove image"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                className="mt-1"
                iconLeft={<ImageIcon className="h-4 w-4" />}
                onClick={() => fileRef.current?.click()}
              >
                Choose an image
              </Button>
            )}
          </div>
        ) : null}

        <label className="block text-sm">
          <span className="font-medium text-fg-muted">
            {mode === 'page' ? 'Describe the page' : 'Source material'}
          </span>
          <textarea
            rows={mode === 'page' ? 3 : 8}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={
              mode === 'page'
                ? 'e.g. A slide on the three fire-alarm signals and what each means'
                : 'Paste the training text, notes or an outline. The AI writes slides then questions.'
            }
            className={SHARED_INPUT_CLASS}
          />
        </label>

        <p className="text-xs text-fg-dim">
          AI-generated content is a starting point — review and edit it before publishing.
        </p>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            iconLeft={<Sparkles className="h-4 w-4" />}
            onClick={() => void generate()}
          >
            Generate
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}
